import type { ExamRef } from '../../domain/examCatalogue';
import type { ServerClient } from '../client';
import { kennung } from './abbildung';
import type { ClassicOsceListe } from './typen';

/**
 * Der Prüfungskatalog des Classic-Servers — `GET /rest/osces`.
 *
 * Anders als Relay' `/rest/v1/exam/list` kommt hier ein **nacktes Array** zurück, ohne Hülle
 * und ohne Gesamtzahl.
 *
 * `current` bildet den Haken „alle anzeigen" des alten Dialogs ab: mit `true` filtert der
 * Server auf `endDate >= heute` und `startDate <= heute + 14 Tage`. Der Classic-Client fragt die
 * Liste standardmässig so ab; das hält sie klein und zeigt dem Prüfer, was ansteht.
 *
 * ⚠️ **Die Liste trägt bei Classic auch die Bausteine mit** (`phrases`, s. Protokollpapier §2 —
 * eine Eigenheit der Jackson-Einstellung des Servers). Sie werden hier nicht einmal gelesen;
 * gebraucht werden sie erst beim Download.
 */

/**
 * „20260915" und „20260917" → „15.09.2026 – 17.09.2026".
 *
 * Eigene Funktion statt `server/katalog.ts::metaZeile`: Relay führt `startDate` + `startTime`
 * (ein Beginn), Classic `startDate` + `endDate` (ein Zeitraum von Tagen, ohne Uhrzeit). Dieselbe
 * Zeile herauszubekommen hiesse, eine Uhrzeit zu erfinden.
 */
export function metaZeile(osce: ClassicOsceListe): string {
  const tag = (wert: string | undefined): string | null =>
    /^\d{8}$/.test(wert ?? '') ? (wert as string).slice(6, 8) + '.' + (wert as string).slice(4, 6) + '.' + (wert as string).slice(0, 4) : null;

  const von = tag(osce.startDate);
  const bis = tag(osce.endDate);
  const zeitraum = von && bis ? (von === bis ? von : von + ' – ' + bis) : (von ?? bis ?? '');

  // Der alte Dialog kennzeichnet eine Prüfung ohne Prüflinge oder ohne Prüfer als nicht
  // benutzbar. Unser `ExamRef` hat dafür kein Feld — der Hinweis steht deshalb in der Metazeile,
  // wo der Prüfer ihn sieht, statt still wegzufallen (Protokollpapier P13).
  const unvollständig =
    osce.examineeCount === 0 || osce.examinerCount === 0 ? 'unvollständig — ohne Prüflinge oder Prüfer' : '';

  const teile = [zeitraum, unvollständig].filter((s) => s !== '');
  if (teile.length === 0) return osce.description ?? '';
  return teile.join(' · ');
}

/**
 * `lastUpdate` als Relay-Zeitstempel — oder gar nicht.
 *
 * ⚠️ **Nicht `alsRelayZeitstempel` aus `abbildung.ts`.** Das setzt bei einem fehlenden oder
 * unbrauchbaren Wert die **eigene Uhr** ein, was beim Download richtig ist (die Zeile muss
 * einen Stand tragen), hier aber falsch wäre: der erfundene Wert läge über jedem echten
 * lokalen Stand, und die Prüfung sähe für immer veraltet aus. Ein fehlender Stand bleibt
 * fehlend — `istVeraltet` schweigt dann (s. `domain/examCatalogue.ts`).
 */
export function stand(lastUpdate: string | undefined): string | undefined {
  const ziffern = (lastUpdate ?? '').replace(/\D/g, '');
  if (ziffern.length >= 17) return ziffern.slice(0, 17);
  if (ziffern.length === 14) return ziffern + '000';
  return undefined;
}

/**
 * Die Prüfungen des Classic-Servers in der Form, die `mergeCatalogue` erwartet.
 *
 * Einträge ohne Kennung oder ohne Namen fallen weg — dieselbe Regel wie beim Relay-Katalog:
 * eine Prüfung ohne Kennung liesse sich nie wiederfinden, und ein erfundener Name stünde im
 * Katalog des Prüfers, als wäre er echt.
 */
export async function holeClassicKatalog(client: ServerClient, nurAktuelle = true): Promise<ExamRef[]> {
  const antwort = await client.get<ClassicOsceListe[]>('/rest/osces', {
    // Ausdrücklich `false`, wie die alte App — nicht weglassen (2026-10-09).
    current: nurAktuelle ? 'true' : 'false',
  });
  const liste = Array.isArray(antwort) ? antwort : [];
  return liste
    .filter((o): o is ClassicOsceListe & { id: number; name: string } =>
      o?.id !== undefined && o.id !== null && typeof o.name === 'string' && o.name !== '',
    )
    .map((o) => ({
      id: kennung.exam(o.id),
      name: o.name,
      meta: metaZeile(o),
      stand: stand(o.lastUpdate),
      // ⚠️ **Classic kennt keine Simulationsprüfung** — der Classic-Server hat kein solches Feld.
      // Hier steht trotzdem ein Wert, weil ein Test darüber wacht, dass beide Modi **dieselbe
      // Form** liefern: sonst merkte die Anzeige am Fehlen eines Feldes den Modus, und genau
      // das soll sie nicht.
      simulativ: false,
      geschuetzt: typeof o.password === 'string' && o.password !== '',
      ...(typeof o.password === 'string' && o.password !== '' ? { klartextPasswort: o.password } : {}),
    }));
}
